import { useEffect, useRef, useId } from "react";
import { Search, X } from "lucide-react";
import { useCommandPalette } from "../../hooks/useCommandPalette";
import { useModal } from "../../hooks/useModal";
import styles from "./CommandPalette.module.css";
export default function CommandPalette() {
  const {
    isOpen,
    setIsOpen,
    query,
    setQuery,
    selectedIndex,
    setSelectedIndex,
    filteredCommands,
    executeCommand,
  } = useCommandPalette();
  const inputRef = useRef<HTMLInputElement>(null),
    resultsRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const { modalRef } = useModal(isOpen, { onClose: () => setIsOpen(false) });
  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
  }, [isOpen]);
  useEffect(() => {
    resultsRef.current
      ?.querySelector(`[data-command-index="${selectedIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);
  if (!isOpen) return null;
  return (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) setIsOpen(false);
      }}
    >
      <div
        className={styles.palette}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search commands"
        tabIndex={-1}
      >
        <div className={styles.searchSection}>
          <div className={styles.searchInputContainer}>
            <Search size={20} aria-hidden="true" />
            <input
              ref={inputRef}
              role="combobox"
              aria-label="Search tools and actions"
              aria-controls={listId}
              aria-expanded="true"
              aria-autocomplete="list"
              aria-activedescendant={
                filteredCommands[selectedIndex]
                  ? `${listId}-${selectedIndex}`
                  : undefined
              }
              className={styles.searchInput}
              placeholder="Type a command or search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="button"
              className={styles.closeSearch}
              aria-label="Close search"
              onClick={() => setIsOpen(false)}
            >
              <X size={20} />
            </button>
          </div>
        </div>
        <div
          className={styles.commandsList}
          ref={resultsRef}
          id={listId}
          role="listbox"
          aria-label="Commands"
        >
          {filteredCommands.map((command, i) => (
            <button
              type="button"
              role="option"
              aria-selected={selectedIndex === i}
              id={`${listId}-${i}`}
              data-command-index={i}
              key={command.id}
              className={`${styles.commandItem} ${selectedIndex === i ? styles.selected : ""}`}
              onMouseEnter={() => setSelectedIndex(i)}
              onClick={() => executeCommand(command)}
            >
              <span className={styles.commandContent}>
                <span className={styles.commandIcon}>{command.icon}</span>
                <span className={styles.commandText}>
                  <span className={styles.commandTitle}>{command.title}</span>
                  {command.description && (
                    <span className={styles.commandDescription}>
                      {command.description}
                    </span>
                  )}
                </span>
              </span>
            </button>
          ))}
          {!filteredCommands.length && (
            <p className={styles.emptyState}>No matching tools or actions.</p>
          )}
        </div>
        <div className={styles.footer}>
          ↑ ↓ to choose · Enter to open · Escape to close
        </div>
      </div>
    </div>
  );
}
